// src/components/Admin/FacilityIntegrationDialog.js
import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Switch,
  FormControlLabel,
  Divider,
  Alert,
  IconButton,
  Tooltip,
  CircularProgress,
  Chip,
  alpha,
  Grid,
  Paper,
  InputAdornment,
  Collapse,
} from '@mui/material';
import {
  Close,
  ContentCopy,
  Wifi,
  Dns,
  CheckCircle,
  RouterOutlined as NetworkIcon,
  PlayArrow as EchoIcon,
  DeleteOutline as RemoveIcon,
  FlashOn as ProvisionIcon,
  VpnKey as KeyIcon,
  Add as AddIcon,
  Visibility as ShowIcon,
  VisibilityOff as HideIcon,
} from '@mui/icons-material';
import { useThemeMode } from '../../contexts/ThemeModeContext';
import {
  getFacilityIntegration,
  updateFacilityIntegration,
  provisionFacilityAccess,
  deprovisionFacilityAccess,
  testFacilityEcho,
} from '../../services/facilities';
import { integrationKeyService } from '../../services/integrationKeys';

const teslaColors = {
  primary: '#3E6AE1',
  secondary: '#34C3FF',
  success: '#4CAF50',
  warning: '#ff9800',
  error: '#f44336',
};

const FieldRow = ({ label, value, onCopy, isDark }) => (
  <Box
    display="flex"
    alignItems="center"
    justifyContent="space-between"
    sx={{
      py: 0.75,
      px: 1.25,
      borderRadius: '8px',
      '&:hover': { background: isDark ? 'rgba(255,255,255,0.04)' : alpha(teslaColors.primary, 0.04) },
    }}
  >
    <Box>
      <Typography variant="caption" sx={{ color: isDark ? '#94A3B8' : '#64748B' }} display="block">
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={600} fontFamily="monospace" sx={{ color: isDark ? '#F1F5F9' : '#0F172A', fontSize: '0.8rem' }}>
        {value || '—'}
      </Typography>
    </Box>
    {value && (
      <Tooltip title="Copy">
        <IconButton size="small" onClick={() => onCopy(value)} sx={{ color: isDark ? '#94A3B8' : '#64748B' }}>
          <ContentCopy fontSize="small" sx={{ fontSize: 15 }} />
        </IconButton>
      </Tooltip>
    )}
  </Box>
);

function FacilityIntegrationDialog({ open, onClose, facility }) {
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';
  const cardBg = isDark ? '#1E1E1E' : '#FFFFFF';
  const borderColor = isDark ? 'rgba(255, 255, 255, 0.08)' : '#E2E8F0';
  const textPrimary = isDark ? '#F1F5F9' : '#0F172A';
  const textSecondary = isDark ? '#94A3B8' : '#64748B';

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [copiedMsg, setCopiedMsg] = useState('');

  // Editable DICOM AE Title
  const [facilityAeTitle, setFacilityAeTitle] = useState('');

  // Editable HL7 fields
  const [hl7Enabled, setHl7Enabled] = useState(false);
  const [emrHost, setEmrHost] = useState('');
  const [emrPort, setEmrPort] = useState(2575);
  const [receivingApp, setReceivingApp] = useState('');
  const [receivingFacility, setReceivingFacility] = useState('');

  // Network / scanner fields
  const [publicIp, setPublicIp] = useState('');
  const [scannerAeTitle, setScannerAeTitle] = useState('');
  const [scannerHost, setScannerHost] = useState('');
  const [scannerPort, setScannerPort] = useState(104);
  const [provisioning, setProvisioning] = useState(false);
  const [deprovisioning, setDeprovisioning] = useState(false);
  const [echoing, setEchoing] = useState(false);
  const [echoResult, setEchoResult] = useState(null);

  // API key state
  const [apiKeys, setApiKeys] = useState([]);
  const [keyLabel, setKeyLabel] = useState('');
  const [showKeyForm, setShowKeyForm] = useState(false);
  const [generatingKey, setGeneratingKey] = useState(false);
  const [newRawKey, setNewRawKey] = useState(null);
  const [showRawKey, setShowRawKey] = useState(false);
  const [keyCopied, setKeyCopied] = useState(false);

  const fetchIntegration = useCallback(async () => {
    if (!facility?._id) return;
    setLoading(true);
    setError('');
    try {
      const result = await getFacilityIntegration(facility._id);
      setData(result);
      setFacilityAeTitle(result.dicom?.callingAeTitle || '');
      setHl7Enabled(!!result.hl7?.enabled);
      setEmrHost(result.hl7?.emrHost || '');
      setEmrPort(result.hl7?.emrPort || 2575);
      setReceivingApp(result.hl7?.receivingApp || 'EMR');
      setReceivingFacility(result.hl7?.receivingFacility || '');
      setPublicIp(result.network?.publicIp || '');
      setScannerAeTitle(result.dicom?.scannerAeTitle || '');
      setScannerHost(result.dicom?.scannerHost || '');
      setScannerPort(result.dicom?.scannerPort || 104);
      setEchoResult(null);
    } catch (err) {
      setError(err.message || 'Failed to load integration settings');
    } finally {
      setLoading(false);
    }
  }, [facility]);

  const fetchApiKeys = useCallback(async () => {
    if (!facility?._id) return;
    try {
      const { data } = await integrationKeyService.list(facility._id);
      setApiKeys(Array.isArray(data) ? data : []);
    } catch { /* non-critical */ }
  }, [facility]);

  useEffect(() => {
    if (open) {
      setError('');
      setSuccess('');
      setNewRawKey(null);
      setShowKeyForm(false);
      setKeyLabel('');
      fetchIntegration();
      fetchApiKeys();
    }
  }, [open, fetchIntegration, fetchApiKeys]);

  const handleGenerateKey = async () => {
    if (!facility?._id) return;
    setGeneratingKey(true);
    try {
      const { data } = await integrationKeyService.generate({
        facilityId:   facility._id,
        facilityName: facility.facilityName,
        label:        keyLabel.trim() || 'EMR integration',
        expiresInDays: 365,
      });
      setNewRawKey(data.apiKey);
      setShowRawKey(false);
      setKeyCopied(false);
      setShowKeyForm(false);
      setKeyLabel('');
      fetchApiKeys();
    } catch {
      setError('Failed to generate API key.');
    } finally {
      setGeneratingKey(false);
    }
  };

  const handleRevokeKey = async (id) => {
    if (!window.confirm('Revoke this API key? The EMR will immediately lose access.')) return;
    try {
      await integrationKeyService.revoke(id);
      fetchApiKeys();
    } catch {
      setError('Failed to revoke key.');
    }
  };

  const handleCopyKey = () => {
    navigator.clipboard.writeText(newRawKey).then(() => {
      setKeyCopied(true);
      setTimeout(() => setKeyCopied(false), 2500);
    });
  };

  const handleCopy = (value) => {
    navigator.clipboard.writeText(String(value));
    setCopiedMsg('Copied!');
    setTimeout(() => setCopiedMsg(''), 1500);
  };

  const handleCopyAllDicom = () => {
    if (!data) return;
    const text =
      `DICOM Modality Worklist — ${data.facilityName}\n` +
      `Worklist Server IP: ${data.dicom.host}\n` +
      `Worklist Server Port: ${data.dicom.port}\n` +
      `Called AE Title: ${data.dicom.calledAeTitle}\n` +
      `This Facility's (Calling) AE Title: ${data.dicom.callingAeTitle}\n\n` +
      `On each scanner, set the Worklist Server to the IP/Port above with Called AE Title "${data.dicom.calledAeTitle}", ` +
      `and set the scanner's own (Local/Calling) AE Title to "${data.dicom.callingAeTitle}".`;
    handleCopy(text);
  };

  const handleCopyAllHl7 = () => {
    if (!data) return;
    const text =
      `HL7 v2 / MLLP Integration — ${data.facilityName}\n` +
      `MLLP Server IP: ${data.hl7.host}\n` +
      `MLLP Server Port: ${data.hl7.port}\n` +
      `Use this as Sending Facility (MSH-4) on outbound orders: ${data.hl7.sendingFacility}\n` +
      `We will send completed reports (ORU) to: ${emrHost || '(not yet configured)'}:${emrPort}\n` +
      `Configure your EMR's inbound listener to expect Receiving App/Facility (MSH-5/6): ${receivingApp} / ${receivingFacility}`;
    handleCopy(text);
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await updateFacilityIntegration(facility._id, {
        hl7: {
          enabled: hl7Enabled,
          emrHost: emrHost.trim(),
          emrPort: Number(emrPort) || 2575,
          receivingApp: receivingApp.trim(),
          receivingFacility: receivingFacility.trim(),
        },
        dicom: {
          aeTitle: facilityAeTitle.trim(),
          scannerAeTitle: scannerAeTitle.trim(),
          scannerHost: scannerHost.trim(),
          scannerPort: Number(scannerPort) || 104,
        },
        network: { publicIp: publicIp.trim() },
      });
      setSuccess('Integration settings saved');
      fetchIntegration();
    } catch (err) {
      setError(err.message || 'Failed to save integration settings');
    } finally {
      setSaving(false);
    }
  };

  const handleProvision = async () => {
    setProvisioning(true);
    setError('');
    setSuccess('');
    try {
      // Save config first, then provision
      await updateFacilityIntegration(facility._id, {
        dicom: { aeTitle: facilityAeTitle.trim(), scannerAeTitle: scannerAeTitle.trim(), scannerHost: scannerHost.trim(), scannerPort: Number(scannerPort) || 104 },
        network: { publicIp: publicIp.trim() },
      });
      const { results } = await provisionFacilityAccess(facility._id);
      const sgMsg = results.sg?.success ? `SG rule added (${results.sg.cidr})` : `SG: ${results.sg?.error}`;
      const orthancMsg = results.orthanc?.success ? `Orthanc registered` : `Orthanc: ${results.orthanc?.error}`;
      setSuccess(`${sgMsg} · ${orthancMsg}`);
      fetchIntegration();
    } catch (err) {
      setError(err.message || 'Provisioning failed');
    } finally {
      setProvisioning(false);
    }
  };

  const handleDeprovision = async () => {
    if (!window.confirm('Remove AWS SG rule and Orthanc registration for this facility?')) return;
    setDeprovisioning(true);
    setError('');
    setSuccess('');
    try {
      await deprovisionFacilityAccess(facility._id);
      setSuccess('Access removed — SG rule revoked and Orthanc modality deleted');
      fetchIntegration();
    } catch (err) {
      setError(err.message || 'Deprovisioning failed');
    } finally {
      setDeprovisioning(false);
    }
  };

  const handleEcho = async () => {
    setEchoing(true);
    setEchoResult(null);
    try {
      const result = await testFacilityEcho(facility._id);
      setEchoResult(result);
    } catch (err) {
      setEchoResult({ success: false, error: err.message });
    } finally {
      setEchoing(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: { xs: '95vw', sm: '92vw', md: '920px' },
          maxWidth: '940px !important',
          maxHeight: '88vh',
          borderRadius: '16px',
          bgcolor: cardBg,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }
      }}
    >
      <DialogTitle sx={{ px: 2.5, py: 1.25, borderBottom: `1px solid ${borderColor}`, bgcolor: cardBg }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="subtitle1" fontWeight={700} sx={{ color: textPrimary, fontSize: '1rem' }}>
              Connect Scanners & EMR
            </Typography>
            <Typography variant="body2" sx={{ color: textSecondary, fontSize: '0.78rem' }}>
              {facility?.facilityName}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ color: textSecondary }}>
            <Close sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 2, overflowY: 'auto', bgcolor: isDark ? '#141414' : '#F8FAFC' }}>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : error && !data ? (
          <Alert severity="error">{error}</Alert>
        ) : data ? (
          <Box>
            {copiedMsg && (
              <Alert severity="success" sx={{ mb: 1.5, py: 0.5, borderRadius: '8px', fontSize: '0.78rem' }}>
                {copiedMsg}
              </Alert>
            )}
            {error && (
              <Alert severity="error" sx={{ mb: 1.5, py: 0.5, borderRadius: '8px', fontSize: '0.78rem' }} onClose={() => setError('')}>
                {error}
              </Alert>
            )}
            {success && (
              <Alert severity="success" sx={{ mb: 1.5, py: 0.5, borderRadius: '8px', fontSize: '0.78rem' }} onClose={() => setSuccess('')}>
                {success}
              </Alert>
            )}

            {/* Top row: DICOM and HL7 side-by-side */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              {/* DICOM Section */}
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.75,
                    borderRadius: '12px',
                    border: `1px solid ${borderColor}`,
                    bgcolor: cardBg,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                    <Dns fontSize="small" sx={{ color: teslaColors.primary }} />
                    <Typography variant="subtitle2" fontWeight={700} sx={{ color: textPrimary, fontSize: '0.88rem' }}>
                      DICOM Worklist (Scanner Setup)
                    </Typography>
                    <Chip label="Always on" size="small" sx={{ ml: 'auto', height: 20, fontSize: '0.68rem', background: alpha(teslaColors.success, 0.1), color: teslaColors.success, fontWeight: 700 }} />
                  </Box>
                  <Typography variant="caption" sx={{ color: textSecondary, display: 'block', mb: 1, fontSize: '0.72rem' }}>
                    Give these values to the facility's imaging engineer to configure scanner queries.
                  </Typography>
                  <Box sx={{ border: '1px solid', borderColor: borderColor, borderRadius: '8px', mb: 1.25, bgcolor: isDark ? '#141414' : '#FAFAFA' }}>
                    <FieldRow label="Worklist Server IP" value={data.dicom.host} onCopy={handleCopy} isDark={isDark} />
                    <Divider sx={{ borderColor }} />
                    <FieldRow label="Worklist Server Port" value={data.dicom.port} onCopy={handleCopy} isDark={isDark} />
                    <Divider sx={{ borderColor }} />
                    <FieldRow label="Called AE Title (our system)" value={data.dicom.calledAeTitle} onCopy={handleCopy} isDark={isDark} />
                  </Box>
                  <TextField
                    fullWidth
                    size="small"
                    label="Facility's Calling AE Title"
                    value={facilityAeTitle}
                    onChange={(e) => setFacilityAeTitle(e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, '').slice(0, 16))}
                    placeholder="e.g. DYC6765"
                    helperText="Must match AE Title set on scanner device. Max 16 chars."
                    sx={{ mb: 1, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                  />
                  <Box sx={{ mt: 'auto', pt: 0.5 }}>
                    <Button size="small" startIcon={<ContentCopy fontSize="small" />} onClick={handleCopyAllDicom} sx={{ borderRadius: '8px', fontSize: '0.75rem', textTransform: 'none' }}>
                      Copy All DICOM Settings
                    </Button>
                  </Box>
                </Paper>
              </Grid>

              {/* HL7 Section */}
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.75,
                    borderRadius: '12px',
                    border: `1px solid ${borderColor}`,
                    bgcolor: cardBg,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                    <Wifi fontSize="small" sx={{ color: teslaColors.primary }} />
                    <Typography variant="subtitle2" fontWeight={700} sx={{ color: textPrimary, fontSize: '0.88rem' }}>
                      HL7 (EMR Integration)
                    </Typography>
                    <FormControlLabel
                      sx={{ ml: 'auto', mr: 0 }}
                      control={<Switch checked={hl7Enabled} onChange={(e) => setHl7Enabled(e.target.checked)} size="small" />}
                      label={<Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.72rem' }}>{hl7Enabled ? 'Enabled' : 'Disabled'}</Typography>}
                    />
                  </Box>
                  <Typography variant="caption" sx={{ color: textSecondary, display: 'block', mb: 1, fontSize: '0.72rem' }}>
                    Connects facility's EMR to send orders (ORM) and receive reports (ORU).
                  </Typography>

                  <Box sx={{ border: '1px solid', borderColor: borderColor, borderRadius: '8px', mb: 1.25, bgcolor: isDark ? '#141414' : '#FAFAFA' }}>
                    <FieldRow label="MLLP Server IP" value={data.hl7.host} onCopy={handleCopy} isDark={isDark} />
                    <Divider sx={{ borderColor }} />
                    <FieldRow label="MLLP Server Port" value={data.hl7.port} onCopy={handleCopy} isDark={isDark} />
                    <Divider sx={{ borderColor }} />
                    <FieldRow label="Sending Facility ID (MSH-4)" value={data.hl7.sendingFacility} onCopy={handleCopy} isDark={isDark} />
                  </Box>

                  <Box display="flex" gap={1} mb={1}>
                    <TextField
                      fullWidth
                      size="small"
                      label="EMR Host / IP"
                      value={emrHost}
                      onChange={(e) => setEmrHost(e.target.value)}
                      placeholder="e.g. 192.168.1.50"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                    />
                    <TextField
                      size="small"
                      label="Port"
                      type="number"
                      value={emrPort}
                      onChange={(e) => setEmrPort(e.target.value)}
                      sx={{ width: 100, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                    />
                  </Box>
                  <Box display="flex" gap={1} mb={1}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Receiving App (MSH-5)"
                      value={receivingApp}
                      onChange={(e) => setReceivingApp(e.target.value)}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label="Receiving Facility (MSH-6)"
                      value={receivingFacility}
                      onChange={(e) => setReceivingFacility(e.target.value)}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                    />
                  </Box>

                  {hl7Enabled && !emrHost && (
                    <Alert severity="warning" sx={{ borderRadius: '8px', mb: 1, py: 0.25, fontSize: '0.72rem' }}>
                      Enter the EMR host before saving with HL7 enabled.
                    </Alert>
                  )}

                  <Box sx={{ mt: 'auto', pt: 0.5 }}>
                    <Button size="small" startIcon={<ContentCopy fontSize="small" />} onClick={handleCopyAllHl7} sx={{ borderRadius: '8px', fontSize: '0.75rem', textTransform: 'none' }}>
                      Copy All HL7 Settings
                    </Button>
                  </Box>
                </Paper>
              </Grid>
            </Grid>

            {/* Middle section: REST API Keys */}
            <Paper
              elevation={0}
              sx={{
                p: 1.75,
                borderRadius: '12px',
                border: `1px solid ${borderColor}`,
                bgcolor: cardBg,
                mb: 2,
              }}
            >
              <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                <KeyIcon fontSize="small" sx={{ color: teslaColors.primary }} />
                <Typography variant="subtitle2" fontWeight={700} sx={{ color: textPrimary, fontSize: '0.88rem' }}>
                  REST API Keys (EMR Integration)
                </Typography>
                <Tooltip title="Generate a new key">
                  <IconButton
                    size="small"
                    sx={{ ml: 'auto', color: teslaColors.primary }}
                    onClick={() => { setShowKeyForm(v => !v); setNewRawKey(null); }}
                  >
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              <Typography variant="caption" sx={{ color: textSecondary, display: 'block', mb: 1.25, fontSize: '0.72rem' }}>
                Issue a scoped API key so this facility's EMR can send orders and retrieve reports via the DycoVue Bridge API.
              </Typography>

              {/* Newly generated key — show once */}
              {newRawKey && (
                <Alert severity="warning" sx={{ borderRadius: '8px', mb: 1.5, py: 0.5, fontSize: '0.78rem' }}>
                  <Typography variant="caption" fontWeight={700} display="block" mb={0.5}>
                    Copy this key now — it won't be shown again.
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    value={showRawKey ? newRawKey : '•'.repeat(Math.min(newRawKey.length, 40))}
                    InputProps={{
                      readOnly: true,
                      sx: { fontFamily: 'monospace', fontSize: 12, bgcolor: isDark ? '#141414' : '#FAFAFA', borderRadius: 1 },
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setShowRawKey(v => !v)}>
                            {showRawKey ? <HideIcon fontSize="small" /> : <ShowIcon fontSize="small" />}
                          </IconButton>
                          <IconButton size="small" onClick={handleCopyKey} color={keyCopied ? 'success' : 'default'}>
                            {keyCopied ? <CheckCircle fontSize="small" /> : <ContentCopy fontSize="small" />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Typography variant="caption" sx={{ color: textSecondary, mt: 0.5, display: 'block', fontSize: '0.72rem' }}>
                    Header: <code>X-API-Key: {newRawKey.slice(0, 10)}…</code>
                    &nbsp;· Bridge: <code>{window.location.origin.replace(':3000', ':5000')}/api/v1/bridge</code> &nbsp;· FHIR R4: <code>{window.location.origin.replace(':3000', ':5000')}/api/fhir/r4</code> &nbsp;· Docs: <code>{window.location.origin.replace(':3000', ':5000')}/api/docs</code>
                  </Typography>
                </Alert>
              )}

              {/* Inline generate form */}
              <Collapse in={showKeyForm}>
                <Box display="flex" gap={1} mb={1.5} alignItems="flex-start">
                  <TextField
                    size="small"
                    fullWidth
                    label="Key label (e.g. Helium Health)"
                    value={keyLabel}
                    onChange={e => setKeyLabel(e.target.value)}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                  />
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleGenerateKey}
                    disabled={generatingKey}
                    startIcon={generatingKey ? <CircularProgress size={14} color="inherit" /> : <KeyIcon />}
                    sx={{ borderRadius: '8px', textTransform: 'none', whiteSpace: 'nowrap', mt: 0.1 }}
                  >
                    Generate
                  </Button>
                </Box>
              </Collapse>

              {/* Existing keys list */}
              {apiKeys.length > 0 ? (
                <Box sx={{ border: '1px solid', borderColor: borderColor, borderRadius: '8px', mb: 1, bgcolor: isDark ? '#141414' : '#FAFAFA' }}>
                  {apiKeys.map((k, i) => (
                    <Box key={k._id}>
                      {i > 0 && <Divider sx={{ borderColor }} />}
                      <Box display="flex" alignItems="center" px={1.5} py={0.75} gap={1}>
                        <Box flex={1} minWidth={0}>
                          <Typography variant="caption" fontFamily="monospace" fontWeight={600} sx={{ color: textPrimary, fontSize: '0.75rem' }}>
                            {k.keyPrefix}…
                          </Typography>
                          <Typography variant="caption" sx={{ color: textSecondary, display: 'block', fontSize: '0.7rem' }}>
                            {k.label} · Last used: {k.lastUsedAt ? new Date(k.lastUsedAt).toLocaleDateString() : 'Never'}
                          </Typography>
                        </Box>
                        <Chip
                          label={k.isActive ? 'Active' : 'Revoked'}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: '0.68rem',
                            bgcolor: alpha(k.isActive ? teslaColors.success : '#999', 0.1),
                            color: k.isActive ? teslaColors.success : '#999',
                            fontWeight: 600,
                          }}
                        />
                        {k.isActive && (
                          <Tooltip title="Revoke key">
                            <IconButton size="small" color="error" onClick={() => handleRevokeKey(k._id)}>
                              <RemoveIcon fontSize="small" sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography variant="caption" sx={{ color: textSecondary, display: 'block', mb: 1, fontSize: '0.72rem' }}>
                  No API keys yet. Click + to generate one.
                </Typography>
              )}
            </Paper>

            {/* Bottom section: Network & Scanner Access */}
            <Paper
              elevation={0}
              sx={{
                p: 1.75,
                borderRadius: '12px',
                border: `1px solid ${borderColor}`,
                bgcolor: cardBg,
              }}
            >
              <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                <NetworkIcon fontSize="small" sx={{ color: teslaColors.primary }} />
                <Typography variant="subtitle2" fontWeight={700} sx={{ color: textPrimary, fontSize: '0.88rem' }}>
                  Network & Scanner Access
                </Typography>
                <Chip
                  label={data.dicom?.isProvisioned ? 'Provisioned' : 'Not provisioned'}
                  size="small"
                  sx={{
                    ml: 'auto',
                    height: 20,
                    fontSize: '0.68rem',
                    background: alpha(data.dicom?.isProvisioned ? teslaColors.success : teslaColors.warning, 0.12),
                    color: data.dicom?.isProvisioned ? teslaColors.success : teslaColors.warning,
                    fontWeight: 700,
                  }}
                />
              </Box>
              <Typography variant="caption" sx={{ color: textSecondary, display: 'block', mb: 1.25, fontSize: '0.72rem' }}>
                Enter the hospital's public IP and scanner details. Click <strong>Provision Access</strong> to configure firewall and Orthanc.
              </Typography>

              <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Hospital Public IP Address"
                    value={publicIp}
                    onChange={(e) => setPublicIp(e.target.value)}
                    placeholder="e.g. 102.88.54.151"
                    helperText="Hospital outbound IP for AWS SG port 4242"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Scanner AE Title (Calling AET)"
                    value={scannerAeTitle}
                    onChange={(e) => setScannerAeTitle(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 16))}
                    placeholder={`e.g. ${data.dicom?.callingAeTitle}`}
                    helperText="Set on scanner console as its own AE Title"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                  />
                </Grid>
                <Grid item xs={12} sm={8}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Scanner Host / IP (for C-ECHO)"
                    value={scannerHost}
                    onChange={(e) => setScannerHost(e.target.value)}
                    placeholder="e.g. 192.168.1.22"
                    helperText="Scanner IP as seen from the Orthanc server"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="DICOM Port"
                    type="number"
                    value={scannerPort}
                    onChange={(e) => setScannerPort(e.target.value)}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                  />
                </Grid>
              </Grid>

              {/* AWS SG status */}
              {data.network?.sgRuleId && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1.5,
                    py: 0.75,
                    borderRadius: '8px',
                    bgcolor: alpha(teslaColors.success, 0.07),
                    mb: 1.5,
                  }}
                >
                  <CheckCircle fontSize="small" sx={{ color: teslaColors.success }} />
                  <Box>
                    <Typography variant="caption" fontWeight={600} color={teslaColors.success}>
                      AWS SG rule active
                    </Typography>
                    <Typography variant="caption" sx={{ color: textSecondary, display: 'block', fontSize: '0.72rem' }}>
                      Rule ID: {data.network.sgRuleId} · IP: {data.network?.publicIp} {data.network.sgProvisionedAt && `· Provisioned: ${new Date(data.network.sgProvisionedAt).toLocaleString()}`}
                    </Typography>
                  </Box>
                </Box>
              )}

              {!data.network?.sgConfigured && (
                <Alert severity="warning" sx={{ borderRadius: '8px', mb: 1.5, py: 0.25, fontSize: '0.72rem' }}>
                  AWS_SECURITY_GROUP_ID is not set on the server — SG rule automation is disabled.
                </Alert>
              )}

              {/* C-ECHO result */}
              {echoResult && (
                <Alert
                  severity={echoResult.success ? 'success' : 'error'}
                  sx={{ borderRadius: '8px', mb: 1.5, py: 0.25, fontSize: '0.72rem' }}
                  onClose={() => setEchoResult(null)}
                >
                  {echoResult.success
                    ? `C-ECHO succeeded — ${echoResult.latencyMs}ms round-trip`
                    : `C-ECHO failed: ${echoResult.error}`}
                </Alert>
              )}

              {/* Action buttons */}
              <Box display="flex" gap={1.25} flexWrap="wrap">
                <Button
                  variant="contained"
                  size="small"
                  startIcon={provisioning ? <CircularProgress size={14} color="inherit" /> : <ProvisionIcon />}
                  onClick={handleProvision}
                  disabled={provisioning || !publicIp.trim()}
                  sx={{
                    borderRadius: '8px',
                    background: `linear-gradient(45deg, ${teslaColors.primary}, ${teslaColors.secondary})`,
                    textTransform: 'none',
                    fontWeight: 600,
                  }}
                >
                  {provisioning ? 'Provisioning...' : 'Provision Access'}
                </Button>

                <Button
                  variant="outlined"
                  size="small"
                  startIcon={echoing ? <CircularProgress size={14} /> : <EchoIcon />}
                  onClick={handleEcho}
                  disabled={echoing || !data.dicom?.isProvisioned}
                  sx={{ borderRadius: '8px', textTransform: 'none' }}
                >
                  {echoing ? 'Testing...' : 'Test Connection (C-ECHO)'}
                </Button>

                {data.dicom?.isProvisioned && (
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    startIcon={deprovisioning ? <CircularProgress size={14} /> : <RemoveIcon />}
                    onClick={handleDeprovision}
                    disabled={deprovisioning}
                    sx={{ borderRadius: '8px', textTransform: 'none', ml: 'auto' }}
                  >
                    {deprovisioning ? 'Removing...' : 'Remove Access'}
                  </Button>
                )}
              </Box>
            </Paper>
          </Box>
        ) : null}
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.25, borderTop: `1px solid ${borderColor}`, bgcolor: cardBg }}>
        <Button onClick={onClose} sx={{ borderRadius: '8px' }}>
          Close
        </Button>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <CheckCircle />}
          onClick={handleSave}
          disabled={saving || loading || !data || (hl7Enabled && !emrHost.trim())}
          sx={{
            borderRadius: '8px',
            px: 2.5,
            background: `linear-gradient(45deg, ${teslaColors.primary}, ${teslaColors.secondary})`,
          }}
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default FacilityIntegrationDialog;
